import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Gauge, Lock, RefreshCw, Save, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { marketingApi } from "../marketingApi";

/* ---------- formatters ---------- */
const fmtTokens = (n) => {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  if (!isFinite(v)) return "—";
  return v.toLocaleString("es-ES");
};
const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid",
    });
  } catch { return iso; }
};
const barColorFor = (pct) =>
  pct == null ? "bg-slate-200"
    : pct >= 100 ? "bg-red-500"
    : pct >= 90 ? "bg-amber-500"
    : pct >= 70 ? "bg-amber-400"
    : "bg-emerald-500";

/**
 * Admin "Presupuesto IA" panel for the Audit tab.
 * - Shows real monthly consumption (Europe/Madrid) + next reset date.
 * - Lets admin set/edit/remove monthly_tokens and toggle enforcement.
 * - Enforcement can only be ON when a budget is defined (hard-gated).
 * - Local confirmation before any save/delete call.
 * - On empty budget: shows "Presupuesto no definido" (never "0 restante").
 */
export default function BudgetPanel({ isAdmin }) {
  const [usage, setUsage] = useState(null);
  const [budget, setBudget] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit form state
  const [draftTokens, setDraftTokens] = useState("");
  const [draftEnforce, setDraftEnforce] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(null); // "save" | "clear" | null

  const load = useCallback(async () => {
    setError(null);
    try {
      const promises = [marketingApi.director.tokenUsage(null)];
      if (isAdmin) {
        promises.push(marketingApi.director.tokenBudgetGet());
        promises.push(marketingApi.director.tokenAlerts());
      } else {
        promises.push(marketingApi.director.tokenAlerts());
      }
      const results = await Promise.all(promises);
      setUsage(results[0]);
      if (isAdmin) {
        setBudget(results[1]);
        setAlerts(results[2]?.alerts || []);
        setDraftTokens(
          results[1]?.monthly_tokens != null ? String(results[1].monthly_tokens) : ""
        );
        setDraftEnforce(!!results[1]?.enforcement_enabled);
      } else {
        setAlerts(results[1]?.alerts || []);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const monthLabel = useMemo(
    () => (usage?.month || "—") + " · Europe/Madrid",
    [usage?.month]
  );
  const monthUsed = usage?.this_month?.total_tokens || 0;
  const monthlyLimit = usage?.budget?.monthly_tokens;
  const remaining = usage?.budget?.remaining_tokens;
  const pct = usage?.budget?.pct_used != null
    ? Math.min(100, Math.round(usage.budget.pct_used * 100))
    : null;
  const budgetDefined = !!usage?.budget?.defined;
  const nextReset = usage?.next_reset_at;

  // Enforce the invariant on the form: unchecking tokens disables enforcement.
  const draftTokensNum = draftTokens.trim() === "" ? null : Number(draftTokens);
  const draftTokensValid = draftTokensNum === null
    || (Number.isInteger(draftTokensNum) && draftTokensNum >= 1 && draftTokensNum <= 10_000_000_000);
  const canEnforce = draftTokensValid && draftTokensNum != null && draftTokensNum > 0;
  useEffect(() => {
    if (!canEnforce && draftEnforce) setDraftEnforce(false);
  }, [canEnforce, draftEnforce]);

  const dirty = (
    (budget?.monthly_tokens ?? null) !== (draftTokensNum ?? null)
    || !!budget?.enforcement_enabled !== !!draftEnforce
  );

  const doSave = async () => {
    setSaving(true);
    try {
      const body = {
        monthly_tokens: draftTokensNum,
        enforcement_enabled: draftTokensNum == null ? false : !!draftEnforce,
        alert_thresholds: budget?.alert_thresholds || [0.7, 0.9, 1.0],
      };
      const r = await marketingApi.director.tokenBudgetSet(body);
      setBudget(r);
      toast.success("Presupuesto actualizado · entrada escrita en auditoría");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`No se pudo guardar: ${detail}`);
    } finally {
      setSaving(false);
      setConfirming(null);
    }
  };

  const doClear = async () => {
    setSaving(true);
    try {
      const r = await marketingApi.director.tokenBudgetSet({
        monthly_tokens: null,
        enforcement_enabled: false,
        alert_thresholds: budget?.alert_thresholds || [0.7, 0.9, 1.0],
      });
      setBudget(r);
      setDraftTokens("");
      setDraftEnforce(false);
      toast.success("Presupuesto quitado · entrada escrita en auditoría");
      await load();
    } catch (e) {
      toast.error(`No se pudo quitar: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
      setConfirming(null);
    }
  };

  const barColor = barColorFor(pct);

  if (loading) {
    return (
      <section className="rounded-2xl border border-[#CCE2F8] bg-white p-5" data-testid="budget-panel-loading">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Gauge size={14} /> Cargando panel de presupuesto IA…
        </div>
      </section>
    );
  }
  if (error) {
    return (
      <section className="rounded-2xl border border-red-200 bg-red-50 p-5" data-testid="budget-panel-error">
        <div className="flex items-center gap-2 text-xs text-red-700">
          <AlertTriangle size={14} /> {error}
        </div>
        <button
          onClick={load}
          className="mt-2 text-[11px] font-semibold text-[#0A2A5E] hover:underline"
          data-testid="budget-panel-retry"
        >
          Reintentar
        </button>
      </section>
    );
  }

  return (
    <section
      className="rounded-2xl border border-[#CCE2F8] bg-white p-5 space-y-4"
      aria-label="Presupuesto mensual del Director IA"
      data-testid="budget-panel"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Gauge size={16} className="text-[#0A2A5E]" />
          <div>
            <h3 className="text-sm font-semibold text-[#0A2A5E]">Presupuesto IA · {monthLabel}</h3>
            <p className="text-[11px] text-slate-500">
              {budgetDefined
                ? `Consumo mensual real reportado por la API del proveedor.`
                : "Consumo mensual real reportado por la API — sin tope definido."}
            </p>
          </div>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1 text-[11px] text-[#0A2A5E] font-semibold hover:underline"
          data-testid="budget-panel-refresh"
          aria-label="Actualizar consumo"
        >
          <RefreshCw size={11} /> Actualizar
        </button>
      </header>

      {/* Progress + figures */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg bg-[#F3F8FE] p-3">
          <div className="text-[11px] text-slate-500">Tokens usados este mes</div>
          <div data-testid="budget-month-used" className="text-2xl font-bold text-slate-800 tabular-nums mt-0.5">
            {fmtTokens(monthUsed)}
          </div>
        </div>
        <div className="rounded-lg bg-[#F3F8FE] p-3">
          <div className="text-[11px] text-slate-500">Límite mensual</div>
          {budgetDefined ? (
            <div data-testid="budget-monthly-limit" className="text-2xl font-bold text-slate-800 tabular-nums mt-0.5">
              {fmtTokens(monthlyLimit)}
            </div>
          ) : (
            <div data-testid="budget-not-defined" className="text-sm font-semibold text-slate-500 italic mt-1">
              Presupuesto no definido
            </div>
          )}
        </div>
        <div className="rounded-lg bg-[#F3F8FE] p-3">
          <div className="text-[11px] text-slate-500">Restante</div>
          {budgetDefined ? (
            <div data-testid="budget-remaining" className="text-2xl font-bold text-slate-800 tabular-nums mt-0.5">
              {fmtTokens(remaining)} <span className="text-xs font-normal text-slate-500">· {pct ?? "—"}%</span>
            </div>
          ) : (
            <div data-testid="budget-remaining-empty" className="text-sm font-semibold text-slate-500 italic mt-1">
              —
            </div>
          )}
        </div>
      </div>

      {budgetDefined && (
        <div className="h-2 w-full rounded bg-slate-100 overflow-hidden" aria-label="Progreso del presupuesto">
          <div
            data-testid="budget-progress-bar"
            className={`h-full ${barColor} transition-all`}
            style={{ width: `${pct == null ? 0 : pct}%` }}
          />
        </div>
      )}

      <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-6 gap-y-1">
        <span>Zona horaria del reinicio: <b>Europe/Madrid</b></span>
        <span>
          Próximo reinicio: <b data-testid="budget-next-reset">{nextReset ? fmtTime(nextReset.utc_iso) : "—"}</b>
          {nextReset?.madrid_local && <span className="text-slate-400"> ({nextReset.madrid_local})</span>}
        </span>
      </div>

      {/* Threshold alerts for the current month */}
      <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-700 mb-2">
          <AlertTriangle size={12} /> Alertas 70/90/100% de este mes
        </div>
        {alerts.length === 0 ? (
          <div className="text-[11px] text-slate-500 italic" data-testid="budget-alerts-empty">
            Sin alertas activas. El Director IA avisará al cruzar cada umbral.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {alerts.map((a) => {
              const t = Math.round((a.threshold || 0) * 100);
              const used = a.used_tokens || 0;
              const total = a.budget_tokens || 0;
              return (
                <li
                  key={a.key}
                  className="flex items-center justify-between gap-2 rounded bg-white border border-amber-200 px-2.5 py-1.5 text-[11px]"
                  data-testid={`budget-alert-${t}`}
                >
                  <span className="font-semibold text-amber-700">Umbral {t}%</span>
                  <span className="tabular-nums text-slate-600">
                    {fmtTokens(used)} / {fmtTokens(total)}
                  </span>
                  <span className="text-slate-400">· {fmtTime(a.created_at)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Non-admin ends here */}
      {!isAdmin && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center gap-2 text-[11px] text-slate-500">
          <Lock size={12} /> Sólo un administrador puede editar el presupuesto.
        </div>
      )}

      {/* Admin edit form */}
      {isAdmin && (
        <div className="rounded-lg border border-[#CCE2F8] bg-[#F3F8FE]/40 p-3 space-y-3" data-testid="budget-admin-form">
          <div className="text-[11px] font-semibold text-[#0A2A5E] uppercase tracking-wide">
            Configuración (admin)
          </div>
          <div className="grid gap-3 md:grid-cols-[1fr_auto]">
            <div>
              <label htmlFor="budget-input" className="block text-[11px] text-slate-500 mb-1">
                Tokens mensuales (vacío = sin presupuesto)
              </label>
              <input
                id="budget-input"
                data-testid="budget-input"
                type="number"
                min="1"
                step="1000"
                value={draftTokens}
                onChange={(e) => setDraftTokens(e.target.value)}
                placeholder="Ej: 500000"
                className="w-full text-sm rounded-lg border border-slate-200 bg-white px-3 py-2 focus:border-[#0A2A5E] focus:outline-none tabular-nums"
                aria-invalid={!draftTokensValid}
              />
              {!draftTokensValid && (
                <div className="mt-1 text-[10px] text-red-600" data-testid="budget-input-error">
                  Debe ser un entero positivo o vacío.
                </div>
              )}
            </div>
            <label className="flex items-end gap-2 text-xs text-slate-700 pb-1">
              <input
                type="checkbox"
                data-testid="budget-enforce-checkbox"
                checked={draftEnforce}
                disabled={!canEnforce}
                onChange={(e) => setDraftEnforce(e.target.checked)}
                className="h-4 w-4 accent-[#0A2A5E]"
              />
              <span className={canEnforce ? "" : "text-slate-400"}>
                Cortar conversaciones al 100%<br/>
                <span className="text-[10px] text-slate-400">
                  (requiere presupuesto definido)
                </span>
              </span>
            </label>
          </div>

          {/* Save confirm */}
          {confirming === "save" && (
            <div className="rounded border border-amber-300 bg-amber-50 p-2.5 text-[11px] text-amber-900" data-testid="budget-confirm-save">
              Vas a <b>guardar</b> {draftTokensNum == null ? "quitando" : `fijando ${fmtTokens(draftTokensNum)} tokens/mes`}{" "}
              y enforcement {draftEnforce ? "ON (bloquea al 100%)" : "OFF (sólo avisos)"}.
              Se escribirá una entrada en auditoría. ¿Confirmar?
              <div className="mt-2 flex gap-2">
                <Button size="sm" onClick={doSave} disabled={saving} className="bg-[#0A2A5E] hover:bg-[#0A2A5E]/90" data-testid="budget-confirm-save-yes">
                  <Save size={12} className="mr-1" /> Sí, guardar
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirming(null)} disabled={saving} data-testid="budget-confirm-save-no">
                  <X size={12} className="mr-1" /> Cancelar
                </Button>
              </div>
            </div>
          )}
          {confirming === "clear" && (
            <div className="rounded border border-red-300 bg-red-50 p-2.5 text-[11px] text-red-900" data-testid="budget-confirm-clear">
              Vas a <b>quitar</b> el presupuesto por completo (quedará "Presupuesto no definido").
              Enforcement se desactivará automáticamente. ¿Confirmar?
              <div className="mt-2 flex gap-2">
                <Button size="sm" onClick={doClear} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="budget-confirm-clear-yes">
                  <Trash2 size={12} className="mr-1" /> Sí, quitar
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirming(null)} disabled={saving} data-testid="budget-confirm-clear-no">
                  <X size={12} className="mr-1" /> Cancelar
                </Button>
              </div>
            </div>
          )}

          {!confirming && (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                disabled={!dirty || !draftTokensValid}
                onClick={() => setConfirming("save")}
                className="bg-[#0A2A5E] hover:bg-[#0A2A5E]/90"
                data-testid="budget-save-btn"
              >
                <Save size={12} className="mr-1" /> Guardar cambios
              </Button>
              {budgetDefined && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirming("clear")}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="budget-clear-btn"
                >
                  <Trash2 size={12} className="mr-1" /> Quitar presupuesto
                </Button>
              )}
              {budget?.updated_at && (
                <span className="text-[10px] text-slate-400 self-center ml-auto">
                  Última edición: {fmtTime(budget.updated_at)}
                </span>
              )}
            </div>
          )}
          <p className="text-[10px] text-slate-400">
            Los créditos reales del proveedor <b>no están disponibles vía API</b>; sólo se mide el uso de tokens
            devuelto por cada llamada. Las claves de proveedor nunca se envían al navegador.
          </p>
        </div>
      )}
    </section>
  );
}
